import React, { useState, useEffect } from 'react';
import { Heart, CreditCard, Building2, Copy, CheckCircle, Loader2, ArrowRight, Shield, Info } from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { useSettings } from '../context/SettingsContext';
import api from '../utils/api';

const PRESETS = [5000, 10000, 25000, 50000, 100000];
const PROGRAMS = ['General Fund','Education','Healthcare','Women Empowerment','Youth Leadership','Environment','Grants'];

const IMPACTS = [
  { amount: 5000,   icon: '🍚', desc: 'Feeds a child for a month' },
  { amount: 10000,  icon: '📚', desc: 'Buys school supplies for a term' },
  { amount: 25000,  icon: '🏥', desc: 'Funds a health screening clinic' },
  { amount: 50000,  icon: '💼', desc: 'Trains a woman entrepreneur' },
  { amount: 100000, icon: '🎓', desc: 'Provides a full scholarship' },
];

export default function DonatePage() {
  const { s } = useSettings();
  const [tab, setTab]       = useState('online'); // 'online' | 'transfer'
  const [form, setForm]     = useState({ donorName:'', email:'', phone:'', amount:'', customAmount:'', program:'General Fund', message:'', isAnonymous:false });
  const [step, setStep]     = useState(1); // 1=form, 2=processing, 3=success, 4=error
  const [copied, setCopied] = useState('');
  const [psLoaded, setPsLoaded] = useState(false);

  const finalAmount = form.amount === 'custom' ? parseInt(form.customAmount || 0) : parseInt(form.amount || 0);
  const paystackKey = s('paystack_public_key', '');

  // Load Paystack script
  useEffect(() => {
    if (document.getElementById('paystack-js')) { setPsLoaded(true); return; }
    const script = document.createElement('script');
    script.id = 'paystack-js';
    script.src = 'https://js.paystack.co/v1/inline.js';
    script.onload = () => setPsLoaded(true);
    document.head.appendChild(script);
  }, []);

  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(''), 2000);
  };

  const recordDonation = async (ref, status) => {
    try {
      await api.post('/donations', {
        donorName: form.isAnonymous ? 'Anonymous' : form.donorName,
        email: form.email, phone: form.phone,
        amount: finalAmount, program: form.program,
        message: form.message, isAnonymous: form.isAnonymous,
        paymentMethod: 'paystack', transactionRef: ref, status,
      });
    } catch (e) { console.error('Record donation error:', e); }
  };

  const handlePaystack = () => {
    if (!finalAmount || finalAmount < 100) return alert('Please enter a valid amount (minimum ₦100)');
    if (!form.email) return alert('Please enter your email address');
    if (!paystackKey) return alert('Online payments are not configured yet. Please use bank transfer or contact admin.');
    if (!psLoaded || !window.PaystackPop) return alert('Payment system is loading. Please try again.');

    const ref = 'SAAI-' + Date.now() + '-' + Math.random().toString(36).substr(2, 6).toUpperCase();
    setStep(2);

    const handler = window.PaystackPop.setup({
      key:       paystackKey,
      email:     form.email,
      amount:    finalAmount * 100, // kobo
      ref,
      currency:  'NGN',
      metadata: { name: form.isAnonymous ? 'Anonymous' : form.donorName, program: form.program, phone: form.phone },
      onClose: () => { setStep(1); },
      callback: async (response) => {
        await recordDonation(response.reference, 'successful');
        setStep(3);
      },
    });
    handler.openIframe();
  };

  const handleBankTransfer = async (e) => {
    e.preventDefault();
    if (!finalAmount || finalAmount < 100) return alert('Please enter a valid amount (minimum ₦100)');
    setStep(2);
    try {
      await recordDonation('TRANSFER-' + Date.now(), 'pending');
      setStep(3);
    } catch { setStep(4); }
  };

  const nearestImpact = IMPACTS.reduce((prev, curr) =>
    Math.abs(curr.amount - finalAmount) < Math.abs(prev.amount - finalAmount) ? curr : prev, IMPACTS[0]);

  return (
    <div className="min-h-screen bg-gray-50 font-body">
      <Navbar />
      <div className="pt-20">
        {/* Hero */}
        <section className="relative bg-hero-gradient py-16 overflow-hidden">
          <div className="absolute inset-0 opacity-20 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDEwIEwgNDAgMTAgTSAxMCAwIEwgMTAgNDAgTSAwIDIwIEwgNDAgMjAgTSAyMCAwIEwgMjAgNDAgTSAwIDMwIEwgNDAgMzAgTSAzMCAwIEwgMzAgNDAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iIzIwMjA2MCIgc3Ryb2tlLXdpZHRoPSIwLjUiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] " />
          <div className="relative max-w-7xl mx-auto px-6 text-center">
            <span className="section-tag border-gold-400/30 text-gold-300 bg-gold-500/10 mb-5">Make a Difference</span>
            <h1 className="font-display text-4xl lg:text-5xl font-bold text-white mb-4">Donate to SAAI Institute</h1>
            <p className="text-white/60 text-lg max-w-xl mx-auto">Your generosity funds education, healthcare, and empowerment programs across Nigeria.</p>
            <div className="flex items-center justify-center gap-6 mt-6 text-sm">
              <div className="flex items-center gap-2 text-emerald-400"><CheckCircle size={16} /> Verified NGO</div>
              <div className="flex items-center gap-2 text-emerald-400"><Shield size={16} /> 94% reaches programs</div>
              <div className="flex items-center gap-2 text-emerald-400"><CheckCircle size={16} /> Tax deductible</div>
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="max-w-6xl mx-auto px-6">
            {step === 3 ? (
              <div className="max-w-lg mx-auto bg-white rounded-3xl shadow-card border border-gray-100 p-12 text-center">
                <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle size={40} className="text-emerald-600" />
                </div>
                <h2 className="font-display text-3xl font-bold text-gray-900 mb-3">Thank You! 🎉</h2>
                <p className="text-gray-500 leading-relaxed mb-2">Your donation of <strong className="text-gray-900">₦{finalAmount.toLocaleString()}</strong> has been received.</p>
                {tab === 'transfer' && <p className="text-gray-500 text-sm mb-6">Once we confirm your transfer, we'll send a receipt to <strong>{form.email}</strong>.</p>}
                {tab === 'online' && <p className="text-gray-500 text-sm mb-6">A receipt has been sent to <strong>{form.email}</strong>. Thank you for your generosity!</p>}
                <button onClick={() => { setStep(1); setForm({ donorName:'',email:'',phone:'',amount:'',customAmount:'',program:'General Fund',message:'',isAnonymous:false }); }}
                  className="btn-royal px-8 py-3">Make Another Donation</button>
              </div>
            ) : (
              <div className="grid lg:grid-cols-5 gap-8">
                {/* Form — 3 cols */}
                <div className="lg:col-span-3 space-y-5">
                  {/* Payment method tabs */}
                  <div className="bg-white rounded-2xl shadow-card border border-gray-100 p-5">
                    <h3 className="font-semibold text-gray-800 mb-4">Choose Payment Method</h3>
                    <div className="grid grid-cols-2 gap-3">
                      <button onClick={() => setTab('online')}
                        className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${tab === 'online' ? 'border-royal-800 bg-royal-50' : 'border-gray-200 hover:border-gray-300'}`}>
                        <CreditCard size={20} className={tab === 'online' ? 'text-royal-800' : 'text-gray-400'} />
                        <div className="text-left">
                          <div className={`font-semibold text-sm ${tab === 'online' ? 'text-royal-800' : 'text-gray-700'}`}>Pay Online</div>
                          <div className="text-xs text-gray-400">Card, bank, USSD</div>
                        </div>
                      </button>
                      <button onClick={() => setTab('transfer')}
                        className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${tab === 'transfer' ? 'border-royal-800 bg-royal-50' : 'border-gray-200 hover:border-gray-300'}`}>
                        <Building2 size={20} className={tab === 'transfer' ? 'text-royal-800' : 'text-gray-400'} />
                        <div className="text-left">
                          <div className={`font-semibold text-sm ${tab === 'transfer' ? 'text-royal-800' : 'text-gray-700'}`}>Bank Transfer</div>
                          <div className="text-xs text-gray-400">Direct transfer</div>
                        </div>
                      </button>
                    </div>
                  </div>

                  {/* Amount */}
                  <div className="bg-white rounded-2xl shadow-card border border-gray-100 p-5">
                    <h3 className="font-semibold text-gray-800 mb-4">Donation Amount</h3>
                    <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-4">
                      {PRESETS.map(p => (
                        <button key={p} onClick={() => setForm(f => ({ ...f, amount: String(p), customAmount: '' }))}
                          className={`py-2.5 rounded-xl text-sm font-semibold border-2 transition-all ${form.amount === String(p) ? 'border-gold-500 bg-gold-50 text-gold-700' : 'border-gray-200 text-gray-600 hover:border-gold-300'}`}>
                          ₦{(p/1000).toFixed(0)}k
                        </button>
                      ))}
                    </div>
                    <div className="relative">
                      <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 font-semibold text-sm">₦</span>
                      <input type="number" min="100" placeholder="Enter custom amount"
                        value={form.amount === 'custom' ? form.customAmount : ''}
                        onChange={e => setForm(f => ({ ...f, amount: 'custom', customAmount: e.target.value }))}
                        onFocus={() => setForm(f => ({ ...f, amount: 'custom' }))}
                        className="w-full pl-8 pr-4 py-3 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-gold-500 transition-all" />
                    </div>
                    {finalAmount >= 100 && (
                      <div className="mt-3 flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 rounded-xl px-4 py-2.5">
                        <span className="text-base">{nearestImpact.icon}</span>
                        <span>Your ₦{finalAmount.toLocaleString()} donation could — <strong>{nearestImpact.desc}</strong></span>
                      </div>
                    )}
                  </div>

                  {/* Program */}
                  <div className="bg-white rounded-2xl shadow-card border border-gray-100 p-5">
                    <h3 className="font-semibold text-gray-800 mb-4">Direct Your Gift <span className="text-gray-400 font-normal text-sm">(optional)</span></h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {PROGRAMS.map(p => (
                        <button key={p} onClick={() => setForm(f => ({ ...f, program: p }))}
                          className={`py-2 px-3 rounded-xl text-xs font-semibold border-2 transition-all text-left ${form.program === p ? 'border-royal-800 bg-royal-50 text-royal-800' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                          {p}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Donor info */}
                  <div className="bg-white rounded-2xl shadow-card border border-gray-100 p-5">
                    <h3 className="font-semibold text-gray-800 mb-4">Your Information</h3>
                    <div className="space-y-4">
                      <label className="flex items-center gap-2.5 cursor-pointer">
                        <input type="checkbox" checked={form.isAnonymous} onChange={e => setForm(f => ({ ...f, isAnonymous: e.target.checked }))}
                          className="w-4 h-4 rounded accent-royal-800" />
                        <span className="text-sm text-gray-600">Donate anonymously</span>
                      </label>
                      {!form.isAnonymous && (
                        <input type="text" placeholder="Full Name" value={form.donorName} onChange={e => setForm(f => ({ ...f, donorName: e.target.value }))} required={!form.isAnonymous}
                          className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-royal-800/20 focus:border-royal-800 transition-all" />
                      )}
                      <div className="grid sm:grid-cols-2 gap-3">
                        <input type="email" placeholder="Email address *" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required
                          className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-royal-800/20 focus:border-royal-800 transition-all" />
                        <input type="tel" placeholder="Phone (optional)" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                          className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-royal-800/20 focus:border-royal-800 transition-all" />
                      </div>
                      <textarea rows={2} placeholder="Message (optional)" value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-royal-800/20 focus:border-royal-800 transition-all resize-none" />
                    </div>
                  </div>

                  {/* Bank Transfer details */}
                  {tab === 'transfer' && (
                    <div className="bg-white rounded-2xl shadow-card border border-gray-100 p-5">
                      <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                        <Building2 size={18} className="text-royal-800" /> Bank Account Details
                      </h3>
                      <div className="bg-gray-50 rounded-xl p-4 space-y-3 mb-5">
                        {[
                          ['Bank Name',       s('bank_name', 'First Bank of Nigeria')],
                          ['Account Name',    s('account_name', 'SAAI Institute Foundation')],
                          ['Account Number',  s('account_number', '0123456789')],
                          ['Sort Code',       s('bank_sort_code', '011')],
                        ].map(([label, value]) => (
                          <div key={label} className="flex items-center justify-between">
                            <span className="text-xs text-gray-500 font-medium">{label}</span>
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-sm text-gray-900 font-mono">{value}</span>
                              <button onClick={() => copyToClipboard(value, label)} className="p-1 hover:bg-gray-200 rounded-lg transition-colors text-gray-400 hover:text-gray-700">
                                {copied === label ? <CheckCircle size={14} className="text-emerald-600" /> : <Copy size={14} />}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl p-3 mb-5">
                        <Info size={14} className="flex-shrink-0 mt-0.5" />
                        Use your <strong>email or name</strong> as the transfer description so we can match your donation. Send your receipt to <a href={`mailto:${s('contact_email','info@saaingo.org')}`} className="underline">{s('contact_email','info@saaingo.org')}</a>
                      </div>
                      <button onClick={handleBankTransfer} disabled={step === 2 || !finalAmount || !form.email}
                        className="w-full btn-royal justify-center py-3.5 disabled:opacity-50">
                        {step === 2 ? <><Loader2 size={16} className="animate-spin" /> Processing...</> : <>I've completed the transfer <ArrowRight size={16} /></>}
                      </button>
                    </div>
                  )}

                  {/* Online Pay button */}
                  {tab === 'online' && (
                    <button onClick={handlePaystack} disabled={step === 2 || !finalAmount || !form.email}
                      className="w-full btn-primary justify-center py-4 text-base disabled:opacity-50 shadow-glow">
                      {step === 2
                        ? <><Loader2 size={18} className="animate-spin" /> Opening payment...</>
                        : <><Heart size={18} className="fill-current" /> Pay ₦{finalAmount ? finalAmount.toLocaleString() : '---'} Securely</>}
                    </button>
                  )}
                </div>

                {/* Sidebar — 2 cols */}
                <div className="lg:col-span-2 space-y-5">
                  <div className="bg-hero-gradient rounded-2xl p-6 text-white">
                    <h3 className="font-display font-bold text-lg mb-4">Your Impact</h3>
                    <div className="space-y-3">
                      {IMPACTS.map(i => (
                        <div key={i.amount} className={`flex items-center gap-3 p-3 rounded-xl transition-all ${finalAmount >= i.amount ? 'bg-white/15' : 'opacity-40'}`}>
                          <span className="text-xl">{i.icon}</span>
                          <div>
                            <div className="font-bold text-sm">₦{i.amount.toLocaleString()}</div>
                            <div className="text-white/60 text-xs">{i.desc}</div>
                          </div>
                          {finalAmount >= i.amount && <CheckCircle size={14} className="text-gold-400 ml-auto flex-shrink-0" />}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-white rounded-2xl shadow-card border border-gray-100 p-5 space-y-4">
                    <h4 className="font-semibold text-gray-800 text-sm">Why Donate to SAAI?</h4>
                    {[
                      { icon: '✅', text: 'Registered with CAC & EFCC' },
                      { icon: '📊', text: '94% of funds reach programs' },
                      { icon: '📝', text: 'Annual audited financial reports' },
                      { icon: '🏆', text: 'NGO of the Year 2023' },
                      { icon: '🔒', text: 'Secure, encrypted payments' },
                    ].map(i => (
                      <div key={i.text} className="flex items-center gap-3 text-sm text-gray-600">
                        <span>{i.icon}</span>{i.text}
                      </div>
                    ))}
                  </div>

                  <div className="bg-gold-50 border border-gold-200 rounded-2xl p-5">
                    <h4 className="font-semibold text-gray-800 text-sm mb-2">Questions?</h4>
                    <p className="text-xs text-gray-500 mb-3">Our team is happy to help with your donation.</p>
                    <a href={`mailto:${s('contact_email','donations@saaingo.org')}`}
                      className="text-xs font-semibold text-royal-800 hover:underline block">
                      {s('contact_email','donations@saaingo.org')}
                    </a>
                    <a href={`tel:${s('contact_phone','+234 800 000 0000')}`}
                      className="text-xs font-semibold text-royal-800 hover:underline block mt-1">
                      {s('contact_phone','+234 800 000 0000')}
                    </a>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
      <Footer />
    </div>
  );
}
